/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { Contract, ethers } from 'ethers';
import { AnimatePresence, motion } from 'framer-motion';
import Button from '../Utils/Button';

interface IOnButtonClick {
  onClick: () => Promise<void> | undefined;
}

interface IProps {
  player1: string;
  player2: string;
  gameStatus: number;
  kickerIndex: number;
  velocity: number;
  isCollectable: boolean;
  isCatcherTimeout: boolean;
  isFetching: boolean;
  contract: Contract;
}

const TnCGameUIButton: React.FC<IProps> = ({
  player1,
  player2,
  gameStatus,
  kickerIndex,
  velocity,
  isCollectable,
  isCatcherTimeout,
  isFetching,
  contract
}: IProps) => {
  // contract definition
  const location = useLocation();
  const path = location.pathname.split('/')[2];

  // states and contexts
  const { account } = useWeb3React<Web3Provider>();
  const [throwInput, setThrowInput] = useState<number>(0.001);
  const [buttonClass, setButtonClass] = useState<string>(
    'buttonOutlined buttonBlue'
  );
  const [buttonText, setButtonText] = useState<string>('JOIN');
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [onButtonClick, setOnButtonClick] = useState<IOnButtonClick>();

  const onCollect = async () => {
    try {
      await contract.distributePrize(path);
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const onCatcherTimeout = async () => {
    try {
      await contract.catcherTimeout(path);
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const onJoin = async () => {
    try {
      await contract.join(path, { value: (10 ** 16).toString() });
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const onStart = async () => {
    try {
      await contract.startGame(path);
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const onThrow = async () => {
    try {
      await contract.kickBall(path, {
        value: (throwInput * 10 ** 18).toString()
      });
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  const onCatch = async () => {
    try {
      await contract.catchBall(path);
    } catch (error: any) {
      if (error.data) {
        window.alert(error.data.message);
      } else {
        window.alert(error.message);
      }
    }
  };

  // utilities
  const calculateBallSpeed = (value: number | undefined) => {
    let ballSpeed;
    if (value) {
      ballSpeed = Math.round(3.003003 * value + 20) / 10;
      if (ballSpeed >= 5) {
        ballSpeed = 5;
      }
    } else {
      ballSpeed = 0;
    }
    return ballSpeed;
  };

  // cancel form submission on throw
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const buttonUpdater = () => {
    const minThrowInput = 0.001;
    // game finished
    if (
      (account && isCollectable && account !== player1 && kickerIndex === 0) ||
      (account && isCollectable && account !== player2 && kickerIndex === 1) ||
      gameStatus === 7
    ) {
      setButtonClass('buttonOutlined buttonGreen');
      setButtonText('COLLECT');
      setOnButtonClick({ onClick: onCollect });
      setButtonDisabled(false);
    } else if (
      (account &&
        isCatcherTimeout &&
        account !== player2 &&
        kickerIndex === 0) ||
      (account && isCatcherTimeout && account !== player1 && kickerIndex === 1)
    ) {
      setButtonClass('buttonOutlined buttonGreen');
      setButtonText('COLLECT');
      setOnButtonClick({ onClick: onCatcherTimeout });
      setButtonDisabled(false);
    }
    // if waiting other player to join
    else if (
      (account === player1 &&
        player2 === ethers.constants.AddressZero &&
        gameStatus === 1) ||
      (account === player2 &&
        player1 === ethers.constants.AddressZero &&
        gameStatus === 1)
    ) {
      setButtonClass('buttonOutlined buttonGreen');
      setButtonText('START');
      setButtonDisabled(true);
    }
    // if visitor and no p2 or p1
    else if (
      (account &&
        account !== player1 &&
        player2 === ethers.constants.AddressZero &&
        gameStatus === 1) ||
      (account &&
        account !== player2 &&
        player1 === ethers.constants.AddressZero &&
        gameStatus === 1)
    ) {
      setButtonClass('buttonOutlined buttonBlue');
      setButtonText('JOIN');
      setOnButtonClick({ onClick: onJoin });
      setButtonDisabled(false);
    }
    // if one of the players and await start
    else if (
      (account === player1 &&
        player2 !== ethers.constants.AddressZero &&
        gameStatus === 1) ||
      (account === player2 &&
        player1 !== ethers.constants.AddressZero &&
        gameStatus === 1)
    ) {
      setButtonClass('buttonOutlined buttonGreen');
      setButtonText('START');
      setOnButtonClick({ onClick: onStart });
      setButtonDisabled(false);
    }
    // if player haven't input coin into the ball or player has thrown the ball and waiting the opponent to catch
    else if (
      (account === player1 &&
        kickerIndex === 0 &&
        velocity === 0 &&
        (throwInput === undefined || throwInput < minThrowInput)) ||
      (account === player2 &&
        kickerIndex === 1 &&
        velocity === 0 &&
        (throwInput === undefined || throwInput < minThrowInput)) ||
      (account === player1 && kickerIndex === 0 && velocity !== 0) ||
      (account === player2 && kickerIndex === 1 && velocity !== 0)
    ) {
      setButtonClass('buttonOutlined buttonBlue');
      setButtonText('THROW');
      setButtonDisabled(true);
    }
    // if a player throwing the ball
    else if (
      (account === player1 && kickerIndex === 0 && velocity === 0) ||
      (account === player2 && kickerIndex === 1 && velocity === 0)
    ) {
      setButtonClass('buttonOutlined buttonBlue');
      setButtonText('THROW');
      setOnButtonClick({ onClick: onThrow });
      setButtonDisabled(false);
    }
    // if awaiting opponent to throw the ball
    else if (
      (account === player1 && kickerIndex === 1 && velocity === 0) ||
      (account === player2 && kickerIndex === 0 && velocity === 0)
    ) {
      setButtonClass('buttonOutlined buttonGolden');
      setButtonText('CATCH');
      setButtonDisabled(true);
    }
    // if catching the ball
    else if (
      (account === player1 && kickerIndex === 1 && velocity !== 0) ||
      (account === player2 && kickerIndex === 0 && velocity !== 0)
    ) {
      setButtonClass('buttonOutlined buttonGolden');
      setButtonText('CATCH');
      setOnButtonClick({ onClick: onCatch });
      setButtonDisabled(false);
    }
    // if a spectator
    else {
      setButtonClass('buttonOutlined buttonBlue');
      setButtonText('JOIN');
      setButtonDisabled(true);
    }
  };

  useEffect(() => {
    buttonUpdater();
  }, [
    account,
    isCollectable,
    isCatcherTimeout,
    gameStatus,
    player1,
    player2,
    kickerIndex,
    velocity,
    throwInput
  ]);

  return (
    <AnimatePresence exitBeforeEnter>
      {isFetching ? (
        <motion.div
          key="tncGameUIButtonLoader"
          variants={{
            collapsed: { opacity: 0 },
            open: {
              opacity: 1
            }
          }}
          initial="collapsed"
          animate="open"
          exit="collapsed"
        >
          <div className="tncGameUIButtonLoader">
            <div className="spinner">
              <div className="spinner-container">
                <svg viewBox="25 25 50 50">
                  <circle
                    cx="50"
                    cy="50"
                    r="20"
                    fill="none"
                    strokeWidth="4"
                    strokeMiterlimit="10"
                  />
                </svg>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="tncGameUIButtonWrapper"
          variants={{
            collapsed: { opacity: 0 },
            open: {
              opacity: 1
            }
          }}
          initial="collapsed"
          animate="open"
          exit="collapsed"
        >
          {buttonText !== 'THROW' ? (
            <div className="tncGameButtonWrapper">
              <Button
                onClick={onButtonClick}
                buttonClass={buttonClass}
                buttonText={buttonText}
                buttonDisabled={buttonDisabled}
              />
            </div>
          ) : (
            <form className="tncGameButtonWrapper" onSubmit={handleSubmit}>
              <div className="tncGameForm">
                <span className="tncGameFormLabel">COIN INPUT</span>
                <div className="tncGameFormInputWrapper">
                  <input
                    type="number"
                    step="any"
                    min="0.001"
                    placeholder="0.001"
                    className="tncGameFormInput"
                    onChange={(e) => setThrowInput(parseFloat(e.target.value))}
                  />
                  <span>BNB</span>
                </div>
              </div>
              <Button
                onClick={onButtonClick}
                buttonClass={buttonClass}
                buttonText={buttonText}
                buttonDisabled={buttonDisabled}
              />
              <div className="tncGameForm">
                <span className="tncGameFormLabel">BALL SPEED</span>
                <div className="tncGameFormInputWrapper">
                  <span className="tncGameFormBallSpeed">
                    {calculateBallSpeed(throwInput)}
                  </span>
                  <span className="tncGameFormUnit">m/block</span>
                </div>
              </div>
            </form>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TnCGameUIButton;
